import {createSlice} from '@reduxjs/toolkit';
import get from 'lodash.get';
import httpClient from '@utils/httpClient';
import {calculateExpiresAt, refreshAccessToken} from '@utils/tokenManager';
import {parseJWT, getIsDelegationToken, getSessionAccountId} from '@utils/jwtUtils';
import {
    BASE_PATH, LOGOUT_PATH,
    OAUTH_PATH
} from '@constants/index';
import {getErrorMessage} from '@utils/requests/error';
import {getAvatarDataUrl} from '@utils/helpers';
import {loadTranslations, setLocale, setTheme} from '@components/appSlice';
import {getUiTheme} from '@constants/themeMapping';
import {fetchDefaultBrand} from '@components/AdminPanel/ThemesAndStyles/brandSettingsSlice';
import {fetchTransactionsSummary} from '@components/Transactions/transactionsSlice';
import {userApi} from '@api/user';
import {configurationApi} from '@api/configuration/api';
import {twoFactorApi, getIsTwoFactorChallenge} from '@api/twoFactor/api';
import {fetchTwoFactorStatus} from '@components/AccountSettings/TwoFactorAuth/twoFactorSlice';
import {signatureApi} from '@api/signature/api';

const HTTP_STATUS_UNAUTHORIZED = 401;
const LOGIN_UNAUTHORIZED_MESSAGE_KEY = 'esl.error.unauthorised.incorrectEmailOrPassword';
const TWO_FACTOR_UNAUTHORIZED_MESSAGE_KEY = 'esl.error.unauthorised.incorrectTwoFactorCode';

const initialUserData = {
    accessToken: null,
    tokenExpiresAt: null,
    isRefreshingToken: false,
    user: {},
    account: {},
    packages: [],
    features: {},
    permissions: [],
    hasUserPermissionOnRoot: false,
    delegationUser: null,
    isDelegationUserFetching: false,
    loginSessionTimeout: 1800000,
    inPerson: false,
    certificateSigning: false,
    isLoading: false,
    authError: null,
    isAuthenticating: false,
    isSavingUser: false,
    saveUserError: null,
    isPasswordReseting: false,
    isPasswordUpdating: false,
    passwordUpdateError: null,
    wasPasswordReset: false,
    passwordResetError: null,
    authenticationToken: null,
    isSessionInfoFetching: false,
    sessionFetchingError: null,
    wasFetchedSession: false,
    senderTokens: [],
    federatedIdentities: [],
    isSSORequired: false,
    isSsoIdpsFetching: false,
    emailSsoIdpsFetchedFor: '',
    ssoIdpsFetchingError: null,
    tokenOldUiLoggedInWith: null,
    activeAccountId: null,
    isFetchingPasswordPolicy: false,
    passwordPolicyError: null,
    isFetchingUserActivationInfo: false,
    userActivationInfoError: null,
    isActivatingUser: false,
    activatingUserError: null,
    isLoggingOut: false,
    imageSignature: {},
    imageParaph: {},
    isFetchingImageSignature: false,
    vsFeatures: [],
    vsAccountFeatures: [],
    twoFactorChallengeToken: null,
    twoFactorUserId: null,
    isTwoFactorRequired: false,
    userAttributes: {},
    isUserAttributesLoading: false,
    userAttributesError: null,
    isUserAttributesSaving: false,
    userAttributesSavingError: null
};

export const userSlice = createSlice({
    name: 'user',
    initialState: initialUserData,
    reducers: {
        setUser: (state, action) => {
            state.user = action.payload;
        },
        setUserAvatar: (state, action) => {
            state.user = {
                ...state.user,
                data: {
                    ...(state.user?.data || {}),
                    avatar: action.payload
                }
            };
        },
        setAccount: (state, action) => {
            state.account = action.payload;
        },
        setPackages: (state, action) => {
            state.packages = action.payload;
        },
        setFeatures: (state, action) => {
            state.features = action.payload;
        },
        setPermissions: (state, action) => {
            state.permissions = action.payload;
        },
        setHasUserPermissionOnRoot: (state, action) => {
            state.hasUserPermissionOnRoot = action.payload;
        },
        setDelegationUser: (state, action) => {
            state.delegationUser = action.payload;
        },
        setIsDelegationUserFetching: (state, action) => {
            state.isDelegationUserFetching = action.payload;
        },
        setLoginSessionTimeout: (state, action) => {
            state.loginSessionTimeout = action.payload;
        },
        setInPerson: (state, action) => {
            state.inPerson = action.payload;
        },
        setCertificateSigning: (state, action) => {
            state.certificateSigning = action.payload;
        },
        setIsLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setAuthError: (state, action) => {
            state.authError = action.payload;
        },
        setIsAuthenticating: (state, action) => {
            state.isAuthenticating = action.payload;
        },
        setIsSavingUser: (state, action) => {
            state.isSavingUser = action.payload;
        },
        setSaveUserError: (state, action) => {
            state.saveUserError = action.payload;
        },
        setWasPasswordReset: (state, action) => {
            state.wasPasswordReset = action.payload;
        },
        setIsPasswordReseting: (state, action) => {
            state.isPasswordReseting = action.payload;
        },
        setPasswordResetError: (state, action) => {
            state.passwordResetError = action.payload;
        },
        setIsPasswordUpdating: (state, action) => {
            state.isPasswordUpdating = action.payload;
        },
        setPasswordUpdateError: (state, action) => {
            state.passwordUpdateError = action.payload;
        },
        setAuthenticationToken: (state, action) => {
            state.authenticationToken = action.payload;
        },
        setSenderTokens: (state, action) => {
            state.senderTokens = action.payload;
        },
        setIsSessionInfoFetching: (state, action) => {
            state.isSessionInfoFetching = action.payload;
        },
        setWasFetchedSession: (state, action) => {
            state.wasFetchedSession = action.payload;
        },
        setFederatedIdentities: (state, action) => {
            state.federatedIdentities = action.payload;
        },
        setIsSSORequired: (state, action) => {
            state.isSSORequired = action.payload;
        },
        setIsSsoIdpsFetching: (state, action) => {
            state.isSsoIdpsFetching = action.payload;
        },
        setSsoIdpsFetchingError: (state, action) => {
            state.ssoIdpsFetchingError = action.payload;
        },
        setEmailSsoIdpsFetchedFor: (state, action) => {
            state.emailSsoIdpsFetchedFor = action.payload;
        },
        setTokenOldUiLoggedInWith: (state, action) => {
            state.tokenOldUiLoggedInWith = action.payload;
        },
        setActiveAccountId: (state, action) => {
            state.activeAccountId = action.payload;
        },
        setIsFetchingPasswordPolicy: (state, action) => {
            state.isFetchingPasswordPolicy = action.payload;
        },
        setPasswordPolicyError: (state, action) => {
            state.passwordPolicyError = action.payload;
        },
        setIsFetchingUserActivationInfo: (state, action) => {
            state.isFetchingUserActivationInfo = action.payload;
        },
        setUserActivationInfoError: (state, action) => {
            state.userActivationInfoError = action.payload;
        },
        setIsActivatingUser: (state, action) => {
            state.isActivatingUser = action.payload;
        },
        setActivatingUserError: (state, action) => {
            state.activatingUserError = action.payload;
        },
        setIsLoggingOut: (state, action) => {
            state.isLoggingOut = action.payload;
        },
        setImageSignature: (state, action) => {
            state.imageSignature = action.payload;
        },
        setImageParaph: (state, action) => {
            state.imageParaph = action.payload;
        },
        setIsFetchingImageSignature: (state, action) => {
            state.isFetchingImageSignature = action.payload;
        },
        setVSFeatures: (state, action) => {
            state.vsFeatures = action.payload;
        },
        setVSAccountFeatures: (state, action) => {
            state.vsAccountFeatures = action.payload;
        },
        setAccessToken: (state, action) => {
            state.accessToken = action.payload;
        },
        setTokenExpiresAt: (state, action) => {
            state.tokenExpiresAt = action.payload;
        },
        setIsRefreshingToken: (state, action) => {
            state.isRefreshingToken = action.payload;
        },
        clearTokens: (state) => {
            state.accessToken = null;
            state.tokenExpiresAt = null;
            state.isRefreshingToken = false;
        },
        resetUserState: (state) => {
            Object.assign(state, initialUserData);
        },
        setTwoFactorChallengeToken: (state, action) => {
            state.twoFactorChallengeToken = action.payload;
        },
        setTwoFactorUserId: (state, action) => {
            state.twoFactorUserId = action.payload;
        },
        setIsTwoFactorRequired: (state, action) => {
            state.isTwoFactorRequired = action.payload;
        },
        clearTwoFactorChallenge: (state) => {
            state.twoFactorChallengeToken = null;
            state.twoFactorUserId = null;
            state.isTwoFactorRequired = false;
        },
        setUserAttributes: (state, action) => {
            state.userAttributes = action.payload;
        },
        setIsUserAttributesLoading: (state, action) => {
            state.isUserAttributesLoading = action.payload;
        },
        setUserAttributesError: (state, action) => {
            state.userAttributesError = action.payload;
        },
        setIsUserAttributesSaving: (state, action) => {
            state.isUserAttributesSaving = action.payload;
        },
        setUserAttributesSavingError: (state, action) => {
            state.userAttributesSavingError = action.payload;
        }
    }
});

export const {
    setUser,
    setUserAvatar,
    setAccount,
    setPackages,
    setPermissions,
    setHasUserPermissionOnRoot,
    setFeatures,
    setDelegationUser,
    setIsDelegationUserFetching,
    setLoginSessionTimeout,
    setInPerson,
    setCertificateSigning,
    setIsLoading,
    setAuthError,
    setIsAuthenticating,
    setIsSavingUser,
    setSaveUserError,
    setWasPasswordReset,
    setIsPasswordReseting,
    setPasswordResetError,
    setIsPasswordUpdating,
    setPasswordUpdateError,
    setAuthenticationToken,
    setSenderTokens,
    setIsSessionInfoFetching,
    setWasFetchedSession,
    setFederatedIdentities,
    setIsSSORequired,
    setIsSsoIdpsFetching,
    setSsoIdpsFetchingError,
    setEmailSsoIdpsFetchedFor,
    setTokenOldUiLoggedInWith,
    setIsFetchingPasswordPolicy,
    setPasswordPolicyError,
    setActiveAccountId,
    setIsFetchingUserActivationInfo,
    setUserActivationInfoError,
    setIsActivatingUser,
    setActivatingUserError,
    setImageSignature,
    setImageParaph,
    setIsFetchingImageSignature,
    setIsLoggingOut,
    setVSFeatures,
    setVSAccountFeatures,
    setAccessToken,
    setTokenExpiresAt,
    setIsRefreshingToken,
    clearTokens,
    resetUserState,
    setTwoFactorChallengeToken,
    setTwoFactorUserId,
    setIsTwoFactorRequired,
    clearTwoFactorChallenge,
    setUserAttributes,
    setIsUserAttributesLoading,
    setUserAttributesError,
    setIsUserAttributesSaving,
    setUserAttributesSavingError
} = userSlice.actions;

export const setSession = (session = {}) => async (dispatch) => {
    dispatch(setUser(session.user));
    dispatch(setAccount(session.account));
    dispatch(setPackages(session.packages));
    dispatch(setFeatures(session.features));
    dispatch(setPermissions(session.permissions));
    dispatch(setHasUserPermissionOnRoot(session.hasUserPermissionOnRoot ?? false));
    dispatch(setDelegationUser(session.delegationUser));
    dispatch(setLoginSessionTimeout(session.loginSessionTimeout));
    dispatch(setInPerson(session.inPerson));
    dispatch(setCertificateSigning(session.certificateSigning));
    dispatch(setActiveAccountId(session.activeAccountId || get(session, 'account.id')));
};

export const fetchUserAvatar = ({userId, options = {}} = {}) => async (dispatch) => {
    if (!userId) {
        return null;
    }
    try {
        const result = await userApi.getAvatar({userId, options});
        const contentType = get(result, ['headers', 'content-type'], '');
        const avatarSrc = getAvatarDataUrl(result.data, contentType);
        dispatch(setUserAvatar(avatarSrc || null));
        return avatarSrc;
    } catch (e) {
        dispatch(setUserAvatar(null));
        return null;
    }
};

export const fetchDelegationUser = () => async (dispatch, getState) => {
    const state = getState();
    const delegationUserId = get(state, 'user.delegationUser.id');
    const accessToken = get(state, 'user.accessToken');

    if (!delegationUserId) {
        return null;
    }

    dispatch(setIsDelegationUserFetching(true));
    try {
        const result = await userApi.getUser({
            userId: delegationUserId,
            options: accessToken ? {accessToken} : {}
        });
        dispatch(setDelegationUser(result.data));
        return result.data;
    } catch (e) {
        console.log('[fetchDelegationUser] failed', e?.response?.status, e?.message);
        return null;
    } finally {
        dispatch(setIsDelegationUserFetching(false));
    }
};

export const fetchSessionInfo = () => async (dispatch) => {
    dispatch(setIsSessionInfoFetching(true));
    dispatch(setWasFetchedSession(false));

    try {
        const url = `${BASE_PATH}/proxy/session`;
        const result = await httpClient.get(url);
        dispatch(setSession(result.data));
        dispatch(fetchUserAvatar({userId: get(result.data, 'user.id')}));
        await dispatch(fetchDelegationUser());
        dispatch(setIsSessionInfoFetching(false));
        dispatch(setWasFetchedSession(true));
        return result.data;
    } catch (err) {
        dispatch(setWasFetchedSession(false));
        dispatch(setIsSessionInfoFetching(false));
        return false;
    }
};

export const fetchUserAttributes = () => async (dispatch, getState) => {
    const userId = get(getState(), 'user.user.id');
    if (!userId) return null;

    dispatch(setIsUserAttributesLoading(true));
    dispatch(setUserAttributesError(null));
    try {
        const result = await userApi.getUserAttributes({userId});
        dispatch(setUserAttributes(result.data));
        return result.data;
    } catch (err) {
        dispatch(setUserAttributesError(getErrorMessage(err)));
        return null;
    } finally {
        dispatch(setIsUserAttributesLoading(false));
    }
};

export const saveUserAttributes = (data) => async (dispatch, getState) => {
    const userId = get(getState(), 'user.user.id');
    if (!userId) return false;

    dispatch(setIsUserAttributesSaving(true));
    dispatch(setUserAttributesSavingError(null));
    try {
        const result = await userApi.updateUserAttributes({userId, data});
        dispatch(setUserAttributes(result.data));
        dispatch(fetchSessionInfo());
        return true;
    } catch (err) {
        dispatch(setUserAttributesSavingError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsUserAttributesSaving(false));
    }
};

export const fetchAuthenticationToken = () => async (dispatch) => {
    try {
        const result = await httpClient.post(`${BASE_PATH}/proxy/authenticationTokens/user`);
        dispatch(setAuthenticationToken(get(result, 'data.value', null)));
        dispatch(setTokenOldUiLoggedInWith(false));
        return result.data;
    } catch (err) {
        return false;
    }
};

export const fetchVSFeatures = ({
    headers, baseUrl, userId, accountId, options = {}
} = {}) => async (dispatch, getState) => {
    const {
        user: userState
    } = getState();
    const {user, account} = userState;
    const uId = userId || user.id;
    const aId = accountId || account?.id;
    const accountLevelServicePath = `api/frontend/features/account/${aId}`;
    const userLevelServicePath = `api/frontend/features/user/${uId}`;
    const accountLevelUrl = baseUrl ? `${baseUrl}/${accountLevelServicePath}` : `${BASE_PATH}/vip/${accountLevelServicePath}`;
    const userLevelUrl = baseUrl ? `${baseUrl}/${userLevelServicePath}` : `${BASE_PATH}/vip/${userLevelServicePath}`;
    const shouldSkip = !uId || !aId;
    const requestOptions = {
        validateStatus: null
    };

    if (headers) {
        requestOptions.headers = headers;
    }

    if (options.timeout) {
        requestOptions.timeout = options.timeout;
    }

    if (shouldSkip) {
        return false;
    }

    try {
        const accountLevelResult = await httpClient.get(accountLevelUrl, requestOptions);
        const userLevelResult = await httpClient.get(userLevelUrl, requestOptions);
        const userFeatures = get(userLevelResult, 'data.data', []);
        const accountFeatures = get(accountLevelResult, 'data.data', []);
        dispatch(setVSFeatures(userFeatures));
        dispatch(setVSAccountFeatures(accountFeatures));

        return {userFeatures, accountFeatures};
    } catch (err) {
        return false;
    }
};

export const authenticate = (options = {}) => async (dispatch) => {
    const payload = options.credentials;

    dispatch(setIsAuthenticating(true));
    dispatch(setAuthError(null));
    dispatch(setPasswordResetError(null));

    try {
        await httpClient.get(`${BASE_PATH}/clearcookie`);
        const result = await httpClient.post(`${BASE_PATH}/proxyAuth`, payload);
        if (result.data) {
            try {
                const session = await dispatch(fetchSessionInfo());
                const attributes = await dispatch(fetchUserAttributes());
                const accountId = get(session, 'activeAccountId');
                if (accountId) {
                    dispatch(fetchDefaultBrand(accountId));
                }
                const locale = get(session, 'user.language');
                const theme = getUiTheme(attributes?.default_theme);
                await dispatch(fetchAuthenticationToken());
                await dispatch(fetchTransactionsSummary());
                dispatch(setLocale({locale, shouldSetCookie: true}));
                dispatch(setTheme({theme, shouldSetCookie: true}));
                dispatch(loadTranslations());
                await dispatch(fetchTwoFactorStatus({isForceRefresh: true}));
                dispatch(setIsAuthenticating(false));
            } catch {
                dispatch(setIsAuthenticating(false));
            }
        }
        return true;
    } catch (err) {
        dispatch(setAuthError(getErrorMessage(err)));
        dispatch(setIsAuthenticating(false));
        return false;
    }
};

export const authenticateWithOAuth = (credentials) => async (dispatch) => {
    dispatch(setIsAuthenticating(true));
    dispatch(setAuthError(null));

    try {
        const oAuthCredentials = {
            username: credentials.email || credentials.username,
            password: credentials.password
        };

        const response = await httpClient.post(`${BASE_PATH}${OAUTH_PATH}/token`, oAuthCredentials);

        // eslint-disable-next-line camelcase
        const {access_token, expires_in} = response.data;

        const expiresAt = calculateExpiresAt(expires_in);
        dispatch(setAccessToken(access_token));
        dispatch(setTokenExpiresAt(expiresAt));

        const session = await dispatch(fetchSessionInfo());
        const attributes = await dispatch(fetchUserAttributes());

        if (session) {
            const accountId = get(session, 'activeAccountId');
            if (accountId) {
                dispatch(fetchDefaultBrand(accountId));
            }
            const locale = get(session, 'user.language');
            const theme = getUiTheme(attributes?.default_theme);

            await dispatch(fetchTransactionsSummary());

            dispatch(setLocale({locale, shouldSetCookie: true}));
            dispatch(setTheme({theme, shouldSetCookie: true}));
            dispatch(loadTranslations());
            await dispatch(fetchTwoFactorStatus({isForceRefresh: true}));
        }

        dispatch(setIsAuthenticating(false));
        return true;
    } catch (err) {
        dispatch(setAuthError(getErrorMessage(err)));
        dispatch(setIsAuthenticating(false));
        return false;
    }
};

export const authenticateWithTwoFactor = (credentials) => async (dispatch) => {
    dispatch(setIsAuthenticating(true));
    dispatch(setAuthError(null));
    dispatch(clearTwoFactorChallenge());

    try {
        const response = await twoFactorApi.login({
            data: {
                email: credentials.email,
                password: credentials.password
            },
            options: {useS2SAuth: true}
        });

        if (getIsTwoFactorChallenge(response)) {
            const {challenge_token: challengeToken, user_id: userId} = response.data;
            dispatch(setTwoFactorChallengeToken(challengeToken));
            dispatch(setTwoFactorUserId(userId));
            dispatch(setIsTwoFactorRequired(true));
            dispatch(setIsAuthenticating(false));
            return {requiresTwoFactor: true};
        }

        const {access_token: accessToken, expires_in: expiresIn} = response.data;
        const expiresAt = calculateExpiresAt(expiresIn);
        dispatch(setAccessToken(accessToken));
        dispatch(setTokenExpiresAt(expiresAt));

        const session = await dispatch(fetchSessionInfo());
        const attributes = await dispatch(fetchUserAttributes());

        if (session) {
            const accountId = get(session, 'activeAccountId');
            if (accountId) {
                dispatch(fetchDefaultBrand(accountId));
            }
            const locale = get(session, 'user.language');
            const theme = getUiTheme(attributes?.default_theme);

            await dispatch(fetchAuthenticationToken());
            await dispatch(fetchTransactionsSummary());

            dispatch(setLocale({locale, shouldSetCookie: true}));
            dispatch(setTheme({theme, shouldSetCookie: true}));
            dispatch(loadTranslations());
            await dispatch(fetchTwoFactorStatus({isForceRefresh: true}));
        }

        dispatch(setIsAuthenticating(false));
        return {success: true};
    } catch (err) {
        const isUnauthorized = err?.response?.status === HTTP_STATUS_UNAUTHORIZED;
        const authError = isUnauthorized
            ? {messageKey: LOGIN_UNAUTHORIZED_MESSAGE_KEY}
            : getErrorMessage(err);
        dispatch(setAuthError(authError));
        dispatch(setIsAuthenticating(false));
        return false;
    }
};

export const verifyTwoFactorLogin = (code) => async (dispatch, getState) => {
    const {twoFactorChallengeToken} = getState().user;

    dispatch(setIsAuthenticating(true));
    dispatch(setAuthError(null));

    try {
        const response = await twoFactorApi.verifyTwoFactor({
            data: {
                challenge_token: twoFactorChallengeToken,
                code
            },
            options: {useS2SAuth: true}
        });

        const {access_token: accessToken, expires_in: expiresIn} = response.data;
        const expiresAt = calculateExpiresAt(expiresIn);
        dispatch(setAccessToken(accessToken));
        dispatch(setTokenExpiresAt(expiresAt));
        dispatch(clearTwoFactorChallenge());

        const session = await dispatch(fetchSessionInfo());
        const attributes = await dispatch(fetchUserAttributes());

        if (session) {
            const accountId = get(session, 'activeAccountId');
            if (accountId) {
                dispatch(fetchDefaultBrand(accountId));
            }
            const locale = get(session, 'user.language');
            const theme = getUiTheme(attributes?.default_theme);

            await dispatch(fetchAuthenticationToken());
            await dispatch(fetchTransactionsSummary());

            dispatch(setLocale({locale, shouldSetCookie: true}));
            dispatch(setTheme({theme, shouldSetCookie: true}));
            dispatch(loadTranslations());
            await dispatch(fetchTwoFactorStatus({isForceRefresh: true}));
        }

        dispatch(setIsAuthenticating(false));
        return true;
    } catch (err) {
        const isUnauthorized = err?.response?.status === HTTP_STATUS_UNAUTHORIZED;
        const authError = isUnauthorized
            ? {messageKey: TWO_FACTOR_UNAUTHORIZED_MESSAGE_KEY}
            : getErrorMessage(err);
        dispatch(setAuthError(authError));
        dispatch(setIsAuthenticating(false));
        return false;
    }
};

export const refreshTokenOnPageLoad = () => async (dispatch, getState) => {
    try {
        const expiredAccessToken = getState()?.user?.accessToken ?? null;
        const tokenData = await refreshAccessToken(expiredAccessToken);

        if (tokenData) {
            dispatch(setAccessToken(tokenData.accessToken));
            dispatch(setTokenExpiresAt(tokenData.expiresAt));
            await dispatch(fetchSessionInfo());
            await dispatch(fetchUserAttributes());
            return true;
        }

        dispatch(clearTokens());
        return false;
    } catch (error) {
        dispatch(clearTokens());
        return false;
    }
};

export const saveUserLanguage = ({userId, language}) => async (dispatch) => {
    dispatch(setIsSavingUser(true));
    dispatch(setSaveUserError(null));

    try {
        await userApi.updateUser({
            userId,
            data: {language}
        });

        await dispatch(fetchSessionInfo());
        await dispatch(fetchUserAttributes());
        dispatch(setIsSavingUser(false));
        return true;
    } catch (err) {
        dispatch(setSaveUserError(getErrorMessage(err)));
        dispatch(setIsSavingUser(false));
        return false;
    }
};

export const resetPassword = (email = '') => async (dispatch) => {
    dispatch(setIsPasswordReseting(true));
    dispatch(setAuthError(null));
    dispatch(setPasswordResetError(null));

    try {
        await userApi.forgotPassword({data: {email}});

        dispatch(setWasPasswordReset(true));
        dispatch(setIsPasswordReseting(false));
        return true;
    } catch (err) {
        dispatch(setPasswordResetError(getErrorMessage(err)));
        dispatch(setWasPasswordReset(false));
        dispatch(setIsPasswordReseting(false));
        return false;
    }
};
export const getActivationInfo = (token) => async (dispatch) => {
    dispatch(setIsFetchingUserActivationInfo(true));
    dispatch(setUserActivationInfoError(null));
    try {
        const url = `${BASE_PATH}/baseProxy/a/register?token=${token}`;
        const {data} = await httpClient.get(url);
        dispatch(setIsFetchingUserActivationInfo(false));

        return data;
    } catch (err) {
        dispatch(setUserActivationInfoError(getErrorMessage(err)));
        dispatch(setIsFetchingUserActivationInfo(false));
        return false;
    }
};

export const activateUser = ({token, form}) => async (dispatch) => {
    dispatch(setIsActivatingUser(true));
    dispatch(setActivatingUserError(null));

    try {
        const url = `${BASE_PATH}/baseProxy/a/register?token=${token}`;
        const {data} = await httpClient.post(url, form);
        dispatch(setIsActivatingUser(false));

        return data;
    } catch (err) {
        dispatch(setIsActivatingUser(false));
        dispatch(setActivatingUserError(getErrorMessage(err)));

        return false;
    }
};
export const updatePassword = (form = {}) => async (dispatch) => {
    dispatch(setIsPasswordUpdating(true));
    dispatch(setPasswordUpdateError(null));

    try {
        await httpClient.put(`${BASE_PATH}/baseProxy/a/password`, form);
        dispatch(setIsPasswordUpdating(false));
        return true;
    } catch (err) {
        dispatch(setIsPasswordUpdating(false));
        dispatch(setPasswordUpdateError(getErrorMessage(err)));

        return false;
    }
};

export const resetPasswordWithToken = ({
    userId, newPassword, newPasswordConfirmation, passwordResetToken
}) => async (dispatch) => {
    dispatch(setIsPasswordUpdating(true));
    dispatch(setPasswordUpdateError(null));

    try {
        await userApi.resetPasswordWithToken({
            userId,
            data: {
                new_password: newPassword,
                new_password_confirmation: newPasswordConfirmation
            },
            params: {
                token: passwordResetToken
            }
        });
        dispatch(setIsPasswordUpdating(false));
        return true;
    } catch (err) {
        dispatch(setIsPasswordUpdating(false));
        dispatch(setPasswordUpdateError(getErrorMessage(err)));
        return false;
    }
};

export const logout = () => async () => {
    try {
        await httpClient.post(`${BASE_PATH}${OAUTH_PATH}/logout`);
    } catch (err) {
        console.error('Logout API call failed:', err);
    }
    return LOGOUT_PATH;
};


export const clearCookie = () => async () => {
    try {
        await httpClient.get(`${BASE_PATH}/clearcookie`);
        return true;
    } catch (err) {
        return false;
    }
};

export const fetchPasswordPolicyByUserId = (userId) => async (dispatch) => {
    dispatch(setIsFetchingPasswordPolicy(true));
    dispatch(setPasswordPolicyError(null));

    try {
        const response = await configurationApi.getUserSecurityPolicy(userId);
        dispatch(setIsFetchingPasswordPolicy(false));
        return response?.data ?? response;
    } catch (err) {
        dispatch(setIsFetchingPasswordPolicy(false));
        dispatch(setPasswordPolicyError(getErrorMessage(err)));
        return false;
    }
};

export const fetchSsoIdps = (email) => async (dispatch, getState) => {
    const {config} = getState().app;

    dispatch(setIsSsoIdpsFetching(true));
    dispatch(setEmailSsoIdpsFetchedFor(''));

    try {
        const encodedEmail = encodeURIComponent(email);
        const url = `${BASE_PATH}/s2s/sso/federated-identities?email=${encodedEmail}`;
        const result = await httpClient.get(url, {
            withCredentials: false,
            timeout: config.VS_ENV === 'development' ? 5000 : undefined
        });

        const federatedIdentities = get(result, 'data.federatedIdentities', []);
        const isSSORequired = get(result, 'data.isSSORequired', false);
        dispatch(setFederatedIdentities(federatedIdentities));
        dispatch(setIsSSORequired(isSSORequired));
        dispatch(setIsSsoIdpsFetching(false));
        dispatch(setEmailSsoIdpsFetchedFor(email));
        return result.data;
    } catch (err) {
        dispatch(setFederatedIdentities([]));
        dispatch(setIsSSORequired(false));
        dispatch(setIsSsoIdpsFetching(false));
        dispatch(setSsoIdpsFetchingError(getErrorMessage(err)));
        dispatch(setEmailSsoIdpsFetchedFor(email));
        return false;
    }
};

export const getImageSignature = (userId) => async (dispatch, getState) => {
    const {user} = getState();
    const id = userId || user.user.id;
    dispatch(setIsFetchingImageSignature(true));

    try {
        const {data} = await signatureApi.getSignature(id);
        dispatch(setImageSignature(data?.image_signature || {}));
        dispatch(setImageParaph(data?.image_paraph || {}));
        dispatch(setIsFetchingImageSignature(false));

        return data;
    } catch (err) {
        dispatch(setIsFetchingImageSignature(false));

        return false;
    }
};

function getFileFromBase64Content(content, fileName) {
    const [header, base64Data] = content.split(',');
    const mimeMatch = header.match(/data:([^;]+)/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';
    const binaryString = window.atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i += 1) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new window.Blob([bytes], {type: mimeType});
    return new window.File([blob], fileName, {type: mimeType});
}

export const saveImageSignature = (imageFile) => async (dispatch, getState) => {
    const {user} = getState();
    dispatch(setIsFetchingImageSignature(true));

    try {
        await signatureApi.storeSignature(user.user.id, {image_signature: imageFile});
        dispatch(setIsFetchingImageSignature(false));
        await dispatch(getImageSignature());

        return true;
    } catch (err) {
        dispatch(setIsFetchingImageSignature(false));
        return false;
    }
};

export const saveImageParaph = (paraphFile) => async (dispatch, getState) => {
    const {user} = getState();
    const existingSignatureContent = user.imageSignature?.content;
    if (!existingSignatureContent) {
        return false;
    }

    dispatch(setIsFetchingImageSignature(true));

    try {
        const signatureFile = getFileFromBase64Content(existingSignatureContent, 'signature.png');
        await signatureApi.storeSignature(user.user.id, {
            image_signature: signatureFile,
            image_paraph: paraphFile
        });
        dispatch(setIsFetchingImageSignature(false));
        await dispatch(getImageSignature());

        return true;
    } catch (err) {
        dispatch(setIsFetchingImageSignature(false));
        return false;
    }
};

export const deleteImageSignature = () => async (dispatch, getState) => {
    const {user} = getState();
    dispatch(setIsFetchingImageSignature(true));
    try {
        await signatureApi.deleteSignature(user.user.id);
        dispatch(setImageSignature({}));
        dispatch(setImageParaph({}));
        dispatch(setIsFetchingImageSignature(false));

        return true;
    } catch (err) {
        dispatch(setIsFetchingImageSignature(false));
        return false;
    }
};

export const selectUser = (state) => state.user.user;
export const selectAccount = (state) => state.user.account;
export const selectPackages = (state) => state.user.packages;
export const selectFeatures = (state) => state.user.features;
export const selectPermissions = (state) => state.user.permissions;
export const selectHasUserPermissionOnRoot = (state) => state.user.hasUserPermissionOnRoot;
export const selectDelegationUser = (state) => state.user.delegationUser;
export const selectIsDelegationUserFetching = (state) => state.user.isDelegationUserFetching;
export const selectLoginSessionTimeout = (state) => state.user.loginSessionTimeout;
export const selectInPerson = (state) => state.user.inPerson;
export const selectCertificateSigning = (state) => state.user.certificateSigning;
export const selectIsLoading = (state) => state.user.isLoading;
export const selectAuthError = (state) => state.user.authError;
export const selectIsAuthenticating = (state) => state.user.isAuthenticating;
export const selectIsPasswordReseting = (state) => state.user.isPasswordReseting;
export const selectPasswordUpdateError = (state) => state.user.passwordUpdateError;
export const selectIsPasswordUpdating = (state) => state.user.isPasswordUpdating;
export const selectWasPasswordReset = (state) => state.user.wasPasswordReset;
export const selectPasswordResetError = (state) => state.user.passwordResetError;
export const selectAuthenticationToken = (state) => state.user.authenticationToken;
export const selectSenderTokens = (state) => state.user.senderTokens;
export const selectIsSessionInfoFetching = (state) => state.user.isSessionInfoFetching;
export const selectWasFetchedSession = (state) => state.user.wasFetchedSession;
export const selectFederatedIdentities = (state) => state.user.federatedIdentities;
export const selectIsSSORequired = (state) => state.user.isSSORequired;
export const selectIsSsoIdpsFetching = (state) => state.user.isSsoIdpsFetching;
export const selectSsoIdpsFetchingError = (state) => state.user.ssoIdpsFetchingError;
export const selectEmailSsoIdpsFetchedFor = (state) => state.user.emailSsoIdpsFetchedFor;
export const selectTokenOldUiLoggedInWith = (state) => state.user.tokenOldUiLoggedInWith;
export const selectActiveAccountId = (state) => {
    const tokenAccountId = getSessionAccountId(state.user.accessToken);
    return tokenAccountId || state.user.activeAccountId;
};
export const selectPasswordPolicyError = (state) => state.user.passwordPolicyError;
export const selectIsFetchingPasswordPolicy = (state) => state.user.isFetchingPasswordPolicy;
export const selectIsFetchingUserActivationInfo = (state) => state.user.isFetchingUserActivationInfo;
export const selectUserActivationInfoError = (state) => state.user.userActivationInfoError;
export const selectIsActivatingUser = (state) => state.user.isActivatingUser;
export const selectActivatingUserError = (state) => state.user.activatingUserError;
export const selectIsLoggingOut = (state) => state.user.isLoggingOut;
export const selectLogoutPath = (state) => state.user.logoutPath;
export const selectSenderExpiryRedirect = (state) => state.user.senderExpiryRedirect;
export const selectImageSignature = (state) => state.user.imageSignature;
export const selectImageParaph = (state) => state.user.imageParaph;
export const selectIsFetchingImageSignature = (state) => state.user.isFetchingImageSignature;
export const selectVSFeatures = (state) => state.user.vsFeatures;
export const selectVSAccountFeatures = (state) => state.user.vsAccountFeatures;
export const selectUserAttributes = (state) => state.user?.userAttributes || {};
export const selectIsUserAttributesLoading = (state) => state.user.isUserAttributesLoading;
export const selectUserAttributesError = (state) => state.user.userAttributesError;
export const selectIsUserAttributesSaving = (state) => state.user.isUserAttributesSaving;
export const selectUserAttributesSavingError = (state) => state.user.userAttributesSavingError;

export const selectAccessToken = (state) => state.user.accessToken;
export const selectTokenExpiresAt = (state) => state.user.tokenExpiresAt;
export const selectIsRefreshingToken = (state) => state.user.isRefreshingToken;
export const selectIsAuthenticated = (state) => !!state.user.accessToken;

export const selectParsedJWT = (state) => {
    const {accessToken} = state.user;
    return parseJWT(accessToken);
};

export const selectGetIsDelegation = (state) => getIsDelegationToken(state.user.accessToken);

export const selectAuthUserRole = (state) => {
    const parsedJWT = selectParsedJWT(state);
    return parsedJWT?.role || null;
};

export const selectJWTPayload = (state) => selectParsedJWT(state);

export const selectJWTEmail = (state) => {
    const parsedJWT = selectParsedJWT(state);
    return parsedJWT?.email || null;
};

export const selectTwoFactorChallengeToken = (state) => state.user.twoFactorChallengeToken;
export const selectTwoFactorUserId = (state) => state.user.twoFactorUserId;
export const selectIsTwoFactorRequired = (state) => state.user.isTwoFactorRequired;

export default userSlice.reducer;
